import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

type ProjectType = 'residential' | 'commercial' | 'industrial' | 'renovation';

const PROJECT_TYPES: { id: ProjectType; label: string; icon: string; desc: string }[] = [
  { id: 'residential', label: 'Residential', icon: 'ri-home-4-line', desc: 'Houses, apartments, villas' },
  { id: 'commercial', label: 'Commercial', icon: 'ri-building-2-line', desc: 'Offices, retail, hospitality' },
  { id: 'industrial', label: 'Industrial', icon: 'ri-building-line', desc: 'Warehouses, factories' },
  { id: 'renovation', label: 'Renovation', icon: 'ri-tools-line', desc: 'Refurbishment projects' },
];

const RADIATOR_TYPES = [
  'Flat Panel Radiators',
  'Vertical Column Radiators',
  'Horizontal Tube Radiators',
  'Towel Rail / Warmer',
  'Designer Radiators',
  'Electric Radiators',
  'Thermostatic Valves',
  'Other / Not Sure',
];

export default function QuotePage() {
  const { getCartCount } = useCart();
  const [projectType, setProjectType] = useState<ProjectType | ''>('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    document.title = 'Request a Bulk Quote | Commercial Radiators | PEL Partner Company';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Request a custom quote for bulk or commercial radiator orders. PEL Partner Company offers volume discounts, priority shipping and dedicated account management across Belgium and Europe.');

    const schemaId = 'schema-quote';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify([
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Request a Bulk Quote — PEL Partner Company',
          description: 'Request a custom quote for bulk or commercial radiator orders. Volume discounts, priority shipping and dedicated account management.',
          url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/request-quote`,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL || 'https://example.com' },
              { '@type': 'ListItem', position: 2, name: 'Request a Quote', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/request-quote` },
            ],
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Bulk & Commercial Radiator Supply',
          provider: {
            '@type': 'Organization',
            name: 'PEL Partner Company',
            url: import.meta.env.VITE_SITE_URL || 'https://example.com',
          },
          description: 'PEL Partner Company offers competitive pricing for bulk radiator orders, commercial fit-outs, and developer projects across Belgium and Europe. Volume discounts on 10+ units, dedicated account manager, priority dispatch.',
          areaServed: ['BE', 'GB', 'EU'],
          serviceType: 'Bulk Radiator Supply',
          offers: {
            '@type': 'Offer',
            description: 'Volume discounts available on orders of 10+ radiator units. Free delivery in Belgium on orders over €3,000 and outside Belgium on orders over €5,000.',
            priceCurrency: 'EUR',
            eligibleQuantity: {
              '@type': 'QuantitativeValue',
              minValue: 10,
            },
          },
        },
      ]);
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const toggleRadiatorType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    const form = e.currentTarget;
    const data = new URLSearchParams();

    const fields = ['fullName', 'email', 'phone', 'company', 'country', 'quantity', 'budget', 'timeline', 'message'];
    fields.forEach((field) => {
      const el = form.elements.namedItem(field) as HTMLInputElement | HTMLTextAreaElement | null;
      if (el) data.append(field, el.value);
    });

    if (projectType) data.append('projectType', projectType);
    if (selectedTypes.length > 0) data.append('radiatorTypes', selectedTypes.join(', '));

    try {
      await fetch('https://readdy.ai/api/form/d7a1jq386jhav3jpftf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f9f9f7]">
        <Navbar cartItemCount={getCartCount()} />
        <div className="pt-32 pb-20 flex items-center justify-center">
          <div className="text-center max-w-lg mx-auto px-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-4xl text-green-600"></i>
            </div>
            <h1 className="text-3xl font-bold text-black mb-4">Quote Request Sent!</h1>
            <p className="text-gray-600 mb-3">
              Thank you for your interest. Our team will review your requirements and get back to you with a tailored quote.
            </p>
            <p className="text-gray-500 text-sm mb-10">
              Typical response time: <strong>1–2 business days</strong>
            </p>
            <div className="bg-white rounded-2xl p-6 mb-8 text-left space-y-3">
              <p className="text-sm font-semibold text-gray-700 mb-4">What happens next?</p>
              {[
                { icon: 'ri-mail-line', text: 'You\'ll receive a confirmation email shortly' },
                { icon: 'ri-user-line', text: 'A specialist will review your project requirements' },
                { icon: 'ri-file-list-3-line', text: 'We\'ll prepare a detailed quote with pricing & lead times' },
                { icon: 'ri-phone-line', text: 'We may call to clarify details if needed' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-gray-600">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0">
                    <i className={`${item.icon} text-gray-500`}></i>
                  </div>
                  {item.text}
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => { setSubmitted(false); setProjectType(''); setSelectedTypes([]); setCharCount(0); }}
                className="px-8 py-4 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Submit Another Request
              </button>
              <a
                href="/"
                className="px-8 py-4 bg-gray-100 text-black rounded-full text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                Back to Home
              </a>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9f9f7]">
      <Navbar cartItemCount={getCartCount()} />

      {/* Hero */}
      <div className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5723ca51a30444e5b4bbddb11e578507.jpg"
            alt="Commercial Radiators"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6">
          <div className="max-w-xl">
            <span className="inline-block bg-white/20 text-white px-4 py-1.5 rounded-full text-xs font-medium uppercase tracking-wider mb-5 backdrop-blur-sm">
              Bulk &amp; Commercial Orders
            </span>
            <h1 className="font-serif text-5xl font-bold text-white mb-4 leading-tight">
              Request a<br />Custom Quote
            </h1>
            <p className="text-white/80 text-base leading-relaxed mb-6">
              Planning a large project? We offer competitive pricing for bulk orders, commercial fit-outs, and developer projects across Belgium and Europe.
            </p>
            <div className="flex flex-wrap gap-4">
              {[
                { icon: 'ri-price-tag-3-line', text: 'Volume discounts available' },
                { icon: 'ri-time-line', text: 'Response within 1–2 business days' },
                { icon: 'ri-customer-service-2-line', text: 'Dedicated account manager' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2 text-white/90 text-sm">
                  <i className={`${item.icon} text-white`}></i>
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-8">

              {/* Contact Details */}
              <div className="bg-white rounded-2xl p-8">
                <h2 className="text-base font-bold text-black mb-6 flex items-center gap-2">
                  <span className="w-7 h-7 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  Your Contact Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      name="fullName"
                      required
                      placeholder="e.g. Thomas Dubois"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Company / Organisation</label>
                    <input
                      type="text"
                      name="company"
                      placeholder="e.g. Dubois Construction"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address <span className="text-red-400">*</span></label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+32 ..."
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Country / Delivery Location <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      name="country"
                      required
                      placeholder="e.g. Belgium, Netherlands, France..."
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa]"
                    />
                  </div>
                </div>
              </div>

              {/* Project Type */}
              <div className="bg-white rounded-2xl p-8">
                <h2 className="text-base font-bold text-black mb-6 flex items-center gap-2">
                  <span className="w-7 h-7 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  Project Type
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {PROJECT_TYPES.map((pt) => (
                    <button
                      key={pt.id}
                      type="button"
                      onClick={() => setProjectType(pt.id)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        projectType === pt.id
                          ? 'border-black bg-black/5'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <div className={`w-10 h-10 flex items-center justify-center rounded-full ${projectType === pt.id ? 'bg-black text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <i className={`${pt.icon} text-lg`}></i>
                      </div>
                      <span className="text-xs font-semibold text-center leading-tight">{pt.label}</span>
                      <span className="text-xs text-gray-400 text-center leading-tight hidden sm:block">{pt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Product Requirements */}
              <div className="bg-white rounded-2xl p-8">
                <h2 className="text-base font-bold text-black mb-6 flex items-center gap-2">
                  <span className="w-7 h-7 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  Product Requirements
                </h2>

                <div className="mb-6">
                  <label className="block text-xs font-semibold text-gray-600 mb-3">Radiator Types Needed <span className="text-gray-400 font-normal">(select all that apply)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {RADIATOR_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleRadiatorType(type)}
                        className={`px-4 py-2 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                          selectedTypes.includes(type)
                            ? 'bg-black text-white border-black'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        {selectedTypes.includes(type) && <i className="ri-check-line mr-1"></i>}
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Estimated Quantity <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      name="quantity"
                      required
                      placeholder="e.g. 20 units, 50+ pieces"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Approximate Budget</label>
                    <input
                      type="text"
                      name="budget"
                      placeholder="e.g. €5,000 – €15,000"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Desired Timeline</label>
                    <input
                      type="text"
                      name="timeline"
                      placeholder="e.g. Within 4 weeks, Q3 2026, ASAP"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa]"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-white rounded-2xl p-8">
                <h2 className="text-base font-bold text-black mb-6 flex items-center gap-2">
                  <span className="w-7 h-7 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  Additional Information
                </h2>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Project Description / Special Requirements</label>
                  <textarea
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your project — room sizes, preferred finishes, specific models, installation requirements, or anything else that helps us prepare an accurate quote..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa] resize-none"
                  ></textarea>
                  <p className={`text-xs mt-1 text-right ${charCount >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
                    {charCount}/500
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-black text-white py-4 rounded-full text-sm font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending Request...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Submit Quote Request
                  </>
                )}
              </button>
              <p className="text-xs text-gray-400 text-center">
                By submitting, you agree to our privacy policy. We never share your data with third parties.
              </p>
            </form>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">

            {/* Why choose us */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="text-sm font-bold text-black mb-5">Why Order in Bulk with Us?</h3>
              <div className="space-y-4">
                {[
                  { icon: 'ri-percent-line', title: 'Volume Discounts', desc: 'Significant savings on orders of 10+ units' },
                  { icon: 'ri-truck-line', title: 'Priority Shipping', desc: 'Dedicated logistics for large orders' },
                  { icon: 'ri-user-star-line', title: 'Account Manager', desc: 'A dedicated contact for your project' },
                  { icon: 'ri-file-list-3-line', title: 'Detailed Quotation', desc: 'Full spec sheet, pricing & lead times' },
                  { icon: 'ri-shield-check-line', title: 'Quality Guarantee', desc: 'All products are CE certified & warranted' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-gray-600`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-black">{item.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact direct */}
            <div className="bg-gray-900 rounded-2xl p-6 text-white">
              <h3 className="text-sm font-bold mb-4">Prefer to Talk Directly?</h3>
              <p className="text-xs text-gray-400 mb-5 leading-relaxed">
                Our commercial team is available Mon–Fri, 9:00–18:00 CET for immediate assistance.
              </p>
              <div className="space-y-3">
                <a
                  href="tel:+32487261970"
                  className="flex items-center gap-3 text-sm text-white hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
                    <i className="ri-phone-line text-sm"></i>
                  </div>
                  +32 487 261 970
                </a>
                <a
                  href="mailto:pelpartnercompany@gmail.com"
                  className="flex items-center gap-3 text-sm text-white hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
                    <i className="ri-mail-line text-sm"></i>
                  </div>
                  pelpartnercompany@gmail.com
                </a>
                <div className="flex items-start gap-3 text-sm text-gray-400">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-sm"></i>
                  </div>
                  <span>Leuvenselaan 467, Tienen 3300, Belgium</span>
                </div>
              </div>
            </div>

            {/* Delivery note */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-truck-line text-green-600 text-lg"></i>
                <h3 className="text-sm font-bold text-green-900">Delivery Info</h3>
              </div>
              <div className="space-y-2 text-xs text-green-700">
                <p><strong>~12 working days</strong> standard delivery</p>
                <p>Free delivery in Belgium on orders over <strong>€3,000</strong></p>
                <p>Free delivery outside Belgium on orders over <strong>€5,000</strong></p>
              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
